import { Toolbar } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import {
  IDocumentMediaPreviewData,
  MESSAGE_CONTENT_MEDIA_TYPE,
  MESSAGE_CONTENT_TYPE,
} from "@home-health-notify/domain";
import { useSnackbar } from "notistack";
import DocumentPreview from "./DocumentPreview";
import MediaViewer from "../mediaViewer/MediaViewer";
import { MediaTitle } from "./DocumentMediaViewer.style";
import MediaPreviewActions from "./MediaPreviewActions";
import { Dialog } from "../../common/modal/Dialog";
import { UserDirectoryContext, useTranslation } from "hhn-commons";

type DocumentMediaViewerProps = {
  dialog: boolean;
  onDocumentMediaDialogClose: () => void;
  selectedMedia: IDocumentMediaPreviewData;
  mediaList?: IDocumentMediaPreviewData[];
};

const DocumentMediaViewer = ({
  dialog,
  onDocumentMediaDialogClose,
  selectedMedia,
  mediaList,
}: DocumentMediaViewerProps) => {
  const [mediaUrl, setMediaUrl] = useState("");
  const [docUrl, setDocUrl] = useState("");
  const { userDirectory } = useContext(UserDirectoryContext);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [activeMedia, setActiveMedia] = useState<
    IDocumentMediaPreviewData | undefined
  >(selectedMedia ?? undefined);

  useEffect(() => {
    selectedMedia && setActiveMedia(selectedMedia);
  }, [selectedMedia]);
  const onDialogClose = () => {
    setActiveMedia(undefined);
    onDocumentMediaDialogClose();
  };
  const onDocumentLoadError = () => {
    enqueueSnackbar(t("message.media.docOpenFailed", { ns: "message" }), {
      variant: "error",
    });
    onDialogClose();
  };
  const messageContentType = activeMedia && activeMedia.mediaType;
  const isDocument = messageContentType === MESSAGE_CONTENT_TYPE.DOCUMENT;

  const shouldOpenMediaViewer =
    mediaList &&
    mediaList.length > 0 &&
    MESSAGE_CONTENT_MEDIA_TYPE.includes(
      messageContentType as MESSAGE_CONTENT_TYPE,
    );

  const userId = activeMedia?.userId;
  let userTitle = activeMedia?.title;
  if (userId) {
    const userInfo = userDirectory.getUserInfo(userId);
    userTitle = userInfo ? userInfo.title : userTitle;
  }
  return (
    <Dialog
      fullScreen
      open={dialog}
      onClose={onDialogClose}
      data-testid="document-media-viewer"
    >
      {activeMedia && (
        <>
          <Toolbar data-testid="toolbar">
            {userTitle && <MediaTitle variant="h6">{userTitle}</MediaTitle>}
            <MediaPreviewActions
              mediaUrl={mediaUrl}
              docUrl={docUrl}
              activeMedia={activeMedia}
              onDialogClose={onDialogClose}
            />
          </Toolbar>
          {shouldOpenMediaViewer && activeMedia && (
            <MediaViewer
              mediaList={mediaList ?? []}
              activeMedia={activeMedia}
              setActiveMedia={
                setActiveMedia as React.Dispatch<
                  React.SetStateAction<IDocumentMediaPreviewData>
                >
              }
              onSetMediaUrl={setMediaUrl}
            />
          )}
          {isDocument && activeMedia && (
            <DocumentPreview
              url={activeMedia.media?.[0]?.url || ""}
              onSetDocUrl={setDocUrl}
              docType={activeMedia.type!}
              onAutoOpen={onDocumentMediaDialogClose}
              onError={onDocumentLoadError}
            />
          )}
        </>
      )}
    </Dialog>
  );
};

export default DocumentMediaViewer;
